import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Snowflake, ShieldCheck, AlertTriangle, RefreshCw, Loader2,
  Activity, Database,
} from 'lucide-react';
import { adminGet } from '../data/api';

const mono = { fontFamily: 'JetBrains Mono, monospace' };
const heading = { fontFamily: 'Outfit, sans-serif' };
const card = { background: '#18181B', border: '1px solid #27272A' };

const REFRESH_MS = 5 * 60 * 1000;

function fmtPct(v) {
  if (v === null || v === undefined) return '—';
  return `${Number(v).toFixed(2)}%`;
}
function fmtNum(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString('it-IT');
}
function fmtDrift(v) {
  if (v === null || v === undefined) return '—';
  return Number(v).toFixed(4);
}

function StatusBadge({ status }) {
  const map = {
    READY: { color: '#10B981', bg: '#064E3B', label: 'READY' },
    WARMING_UP: { color: '#F59E0B', bg: '#78350F', label: 'WARMING UP' },
    LOOKAHEAD_ANOMALY: { color: '#EF4444', bg: '#7F1D1D', label: 'LOOKAHEAD ANOMALY' },
  };
  const cfg = map[status] || { color: '#A1A1AA', bg: '#27272A', label: status || '—' };
  return (
    <span
      data-testid="warmup-status-badge"
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium"
      style={{ ...mono, color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.color}33` }}
    >
      <Activity size={11} /> {cfg.label}
    </span>
  );
}

function StatCard({ label, value, sublabel, icon: Icon, testid, color = 'sky' }) {
  return (
    <div data-testid={testid} className="rounded-sm p-4" style={card}>
      <div className="flex items-start justify-between mb-2">
        <div className="text-xs uppercase tracking-wider text-zinc-500" style={heading}>
          {label}
        </div>
        {Icon && <Icon size={14} className={`text-${color}-400`} />}
      </div>
      <div className="text-2xl font-bold text-zinc-100" style={mono}>
        {value}
      </div>
      {sublabel && (
        <div className="text-xs text-zinc-500 mt-1" style={mono}>{sublabel}</div>
      )}
    </div>
  );
}

function MarketRow({ market, data, days = 7 }) {
  if (!data) return null;
  return (
    <tr className="border-t border-zinc-800" data-testid={`market-row-${market}`}>
      <td className="py-2 px-3 text-zinc-300 font-medium" style={heading}>
        {market}
        {data.partial && (
          <span data-testid={`market-partial-${market}`}
                title="Total denominator was inconsistent (count_documents fallback). Coverage % may be approximate — check backend logs."
                className="ml-1.5 text-[9px] uppercase tracking-wider text-amber-300 align-middle"
                style={mono}>
            partial
          </span>
        )}
      </td>
      <td className="py-2 px-3 text-zinc-400 text-xs" style={mono}>{data.collection}</td>
      <td className="py-2 px-3 text-right text-zinc-200" style={mono}>{fmtNum(data.total_rows)}</td>
      <td className="py-2 px-3 text-right text-zinc-200" style={mono}>{fmtNum(data.rows_with_snapshot)}</td>
      <td className="py-2 px-3 text-right text-emerald-300" style={mono}>{fmtPct(data.snapshot_coverage_pct)}</td>
      <td className="py-2 px-3 text-right text-zinc-400" style={mono}>{fmtPct(data.missing_legacy_pct)}</td>
      <td className="py-2 px-3 text-right text-amber-300" style={mono}>{fmtNum(data.frozen_rows_last_24h)}</td>
      <td className="py-2 px-3 text-right text-rose-300" style={mono}>
        {fmtNum(data[`lookahead_warning_count_${days}d`])}
      </td>
      <td className="py-2 px-3 text-right text-sky-300" style={mono}>{fmtNum(data.safe_rows_available_for_backtest)}</td>
    </tr>
  );
}

export default function AdminFeatureSnapshotCoverage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [days, setDays] = useState(7);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const pin = sessionStorage.getItem('admin_pin') || '';
      const r = await adminGet(
        `/tt/admin/feature-snapshot-coverage?days=${days}`,
        pin,
      );
      setData(r);
      setLastRefresh(new Date());
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const g = data?.global || {};
  const pm = data?.per_market || {};

  return (
    <div className="space-y-5 fade-in" data-testid="admin-feature-snapshot-coverage">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            data-testid="back-to-admin"
            className="flex items-center gap-1 text-zinc-500 hover:text-zinc-200 text-sm"
          >
            <ArrowLeft size={14} /> Admin
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-zinc-50 tracking-tight" style={heading}>
              Feature Snapshot Coverage
            </h1>
            <p className="text-zinc-400 mt-1 text-xs" style={mono}>
              Read-only barometer · pre-match feature freeze warm-up
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={data?.warmup_status} />
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="window-days-select"
            className="px-2 py-1.5 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs"
            style={mono}
          >
            <option value={1}>1d</option>
            <option value={7}>7d</option>
            <option value={14}>14d</option>
            <option value={30}>30d</option>
          </select>
          <button
            onClick={load}
            disabled={loading}
            data-testid="refresh-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs text-zinc-400 hover:text-emerald-300 disabled:opacity-50"
            style={{ border: '1px solid #27272A' }}
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh
          </button>
        </div>
      </div>

      {err && (
        <div
          data-testid="error-banner"
          className="flex items-center gap-2 px-3 py-2 rounded-sm text-xs text-rose-300"
          style={{ ...mono, background: '#450A0A', border: '1px solid #7F1D1D' }}
        >
          <AlertTriangle size={12} /> {err}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          testid="stat-coverage-pct"
          label="Snapshot Coverage"
          value={fmtPct(g.snapshot_coverage_pct)}
          sublabel="frozen / total eval rows"
          icon={Snowflake}
          color="emerald"
        />
        <StatCard
          testid="stat-frozen-24h"
          label="Frozen Rows · 24h"
          value={fmtNum(g.frozen_rows_last_24h)}
          sublabel="new snapshots written"
          icon={Database}
          color="amber"
        />
        <StatCard
          testid="stat-lookahead-window"
          label={`Lookahead · ${data?.window_days || days}d`}
          value={fmtNum(g[`lookahead_warning_count_${data?.window_days || days}d`])}
          sublabel={`share ${fmtPct(g.lookahead_share_pct)}`}
          icon={AlertTriangle}
          color={g.lookahead_share_pct > 5 ? 'rose' : 'zinc'}
        />
        <StatCard
          testid="stat-safe-rows"
          label="Safe Settled Rows"
          value={fmtNum(g.safe_rows_available_for_backtest)}
          sublabel="lookahead_warning = false"
          icon={ShieldCheck}
          color="sky"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <StatCard
          testid="stat-missing-legacy"
          label="Missing Legacy"
          value={fmtPct(g.missing_legacy_pct)}
          sublabel="pre-rollout rows · no snapshot"
          color="zinc"
        />
        <StatCard
          testid="stat-drift-avg"
          label="Snapshot vs Live drift"
          value={fmtDrift(g.snapshot_vs_current_market_prob_p1_drift_avg)}
          sublabel={`avg |Δ market_prob_p1| · n=${fmtNum(g.drift_sample_size)}`}
          color="sky"
        />
      </div>

      <div className="rounded-sm overflow-hidden" style={card}>
        <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-zinc-500" style={heading}>
            Per-Market Breakdown
          </div>
          <div className="text-[10px] text-zinc-600" style={mono}>
            {lastRefresh ? `last refresh ${lastRefresh.toLocaleTimeString('it-IT')}` : ''}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="per-market-table">
            <thead>
              <tr className="text-zinc-500 text-[10px] uppercase tracking-wider">
                <th className="py-2 px-3 text-left" style={heading}>Market</th>
                <th className="py-2 px-3 text-left" style={heading}>Collection</th>
                <th className="py-2 px-3 text-right" style={heading}>Total</th>
                <th className="py-2 px-3 text-right" style={heading}>Frozen</th>
                <th className="py-2 px-3 text-right" style={heading}>Coverage</th>
                <th className="py-2 px-3 text-right" style={heading}>Legacy</th>
                <th className="py-2 px-3 text-right" style={heading}>24h</th>
                <th className="py-2 px-3 text-right" style={heading}>Lookahead</th>
                <th className="py-2 px-3 text-right" style={heading}>Safe</th>
              </tr>
            </thead>
            <tbody>
              <MarketRow market="HC" data={pm.HC} days={days} />
              <MarketRow market="OU" data={pm.OU} days={days} />
              <MarketRow market="MW" data={pm.MW} days={days} />
            </tbody>
          </table>
        </div>
      </div>

      <div
        className="rounded-sm p-3 text-xs text-zinc-500"
        style={{ ...mono, ...card }}
        data-testid="meta-line"
      >
        computed_at: {data?.computed_at || '—'} · window_days: {data?.window_days || '—'} ·
        warmup_status: <span className="text-zinc-300">{data?.warmup_status || '—'}</span>
      </div>

      <div
        className="rounded-sm p-3 text-[11px] text-zinc-500 leading-relaxed"
        style={{ ...mono, ...card }}
      >
        <span className="text-amber-400">Note:</span> read-only widget. No pipeline/strategy changes,
        no Telegram on PASS, no automatic gating. Strategy T re-validation gated on warm-up:
        <span className="text-zinc-300"> safe_settled ≥ 1k</span> AND
        <span className="text-zinc-300"> lookahead_share ≤ 5%</span> AND
        <span className="text-zinc-300"> market mapping audit PASS</span>.
      </div>
    </div>
  );
}
